import React, { useContext, useLayoutEffect, useRef } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';
import classNames from 'classnames';

import { LessonMode } from '../../../Constants';
import { register } from '../../../i18n';

import useAccessibilityClick from '../../../hooks/useAccessibilityClick';
import leftRailService, { LeftRailTab } from '../../services/LeftRailService';
import navMenuService from '../../services/NavMenuService';

import SatelliteToggle from '../tabbedFlyout/SatelliteToggle';
import Flyout from '../tabbedFlyout/Flyout';
import { DirectionsPanel, DirectionsTab } from '../tabbedFlyout/DirectionsFlyout';
import VerticalGotoMenu from './VerticalGotoMenu';

import '../../../css/LeftRail.scss';

const t = register('NavigationBar');
const t2 = register('AriaLabels');

/**
 * Unified left-edge rail: one full-height panel and one tab strip. Opening any
 * tab shows its panel and slides every tab to the panel's right edge. Clicking
 * the selected tab closes the rail. Related Resources is the only wider panel.
 * Alignments hosted here are the parent activity's only; standalone and child
 * item alignments/rubric stay on the right-side flyout.
 */
const LeftRail = observer(({ hostedFlyout } = {}) => {
  const {
    lessonManager,
    navMenuManager
  } = useContext(MobXProviderContext);

  const gotoTabRef = useRef();
  const showVerticalGoto = !!lessonManager.lessonOptions?.hasVerticalGotoMenu;
  const showRelated = !!lessonManager.shouldShowRelatedTabButton &&
    lessonManager.playerMode !== LessonMode.TEACHER_PRESENTATION;
  const directions = navMenuManager.leftRailDirections;
  const hasDirections = !!directions;
  const selectedTab = navMenuManager.leftRailSelectedTab;
  const isOpen = leftRailService.isOpen;
  const isSatellite = selectedTab === LeftRailTab.SATELLITE && showRelated;
  const hasHostedFlyout = !!hostedFlyout;

  useAccessibilityClick(gotoTabRef, (event) => {
    navMenuService.gotoButtonHandler(event);
  });

  // Keep the open panel in sync when navigation removes the previously selected
  // tab (directions / related / activity flyout). Otherwise the rail stays open
  // with an empty column until the user clicks GO TO again.
  useLayoutEffect(() => {
    leftRailService.reconcileSelectedTab({ hasFlyout: hasHostedFlyout });
  }, [isOpen, selectedTab, showVerticalGoto, showRelated, hasDirections, hasHostedFlyout]);

  const hasTabs = showVerticalGoto || showRelated || hasDirections || hasHostedFlyout;
  if (!hasTabs) {
    return null;
  }

  return (
    <div
      className={classNames('left-rail', {
        open: isOpen,
        closed: !isOpen,
        satellite: isSatellite
      })}>
      <div
        className='left-rail-panel'
        id='left-rail-panel'>
        {isOpen && selectedTab === LeftRailTab.GOTO && showVerticalGoto && (
          <VerticalGotoMenu panelOnly />
        )}
        {isOpen && selectedTab === LeftRailTab.DIRECTIONS && directions && (
          <DirectionsPanel
            directions={directions.directions}
            lessonElementId={directions.lessonElementId} />
        )}
        {isOpen && selectedTab === LeftRailTab.FLYOUT && hostedFlyout && (
          <Flyout
            flyoutSide={hostedFlyout.flyoutSide}
            isActivity={hostedFlyout.isActivity}
            isScoring={hostedFlyout.isScoring}
            lessonElementId={hostedFlyout.lessonElementId}
            variant='panel' />
        )}
      </div>
      <div className='left-rail-tabs' role='tablist'>
        {showVerticalGoto && (
          <div
            ref={gotoTabRef}
            aria-controls='left-rail-panel'
            aria-expanded={isOpen && selectedTab === LeftRailTab.GOTO}
            aria-label={isOpen && selectedTab === LeftRailTab.GOTO ? t2('closethevavigationmenu') : t2('showthevavigationmenu')}
            aria-selected={isOpen && selectedTab === LeftRailTab.GOTO}
            className={classNames('vertical-goto-tab', {
              selected: isOpen && selectedTab === LeftRailTab.GOTO
            })}
            role='tab'
            tabIndex='0'>
            <span className='vertical-goto-tab-label'>{t('goto')}</span>
          </div>
        )}
        {showRelated && <SatelliteToggle />}
        {directions && (
          <DirectionsTab
            lessonElementId={directions.lessonElementId}
            selected={isOpen && selectedTab === LeftRailTab.DIRECTIONS} />
        )}
        {hostedFlyout && (
          <Flyout
            flyoutSide={hostedFlyout.flyoutSide}
            isActivity={hostedFlyout.isActivity}
            isScoring={hostedFlyout.isScoring}
            lessonElementId={hostedFlyout.lessonElementId}
            variant='tabs' />
        )}
      </div>
    </div>
  );
});

export default LeftRail;
